import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';
import { hasChildFieldValue } from '../../../../../../common';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { ChildBackgroundType } from '../../../../../../common';

export default function useIntakeChildBackground() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();

  const childInfo = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.child_info,
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createChildBackgroundLoading ||
      state.intakeQuestionnaire.deleteChildBackgroundLoading,
  );

  const create = useCallback(
    async (backgroundType: ChildBackgroundType, value: string) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeChildBackground({
          intakeQuestionnaireId,
          data: { background_type: backgroundType, background_description: value },
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeChildBackground.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (backgroundId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeChildBackground({
          intakeQuestionnaireId,
          backgroundId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeChildBackground.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    backgrounds: childInfo?.backgrounds ?? [],
    hasSavedValues: hasChildFieldValue(childInfo),
    isSaving,
    create,
    remove,
  };
}
